<template>
	<div
		class="project-preview"
		:class="project.cssClass"
	>
		<div
			class="project-preview__bg_back"
			v-if="project.previewBgImg_back"
			:style="{ 'background-image': `url(${project.previewBgImg_back})` }"
		></div>
		<div
			class="project-preview__bg_front"
			v-if="project.previewBgImg_front"
			:style="{ 'background-image': `url(${project.previewBgImg_front})` }"
		></div>

		<div class="project-preview__inactivity_mask"></div>

		<div class="project-preview__tags">
			<span
				v-for="(tag, i) in project.tags"
				:key="i"
				>{{ '#' + tag }}</span
			>
		</div>

		<div class="project-preview__name">
			<div
				class="text"
				v-if="project.previewText"
			>
				{{ project.previewText }}
			</div>
			<div class="name">{{ project.name }}</div>
			<div
				class="desc"
				v-if="project.previewDescription"
			>
				{{ project.previewDescription }}
			</div>

			<router-link
				class="project-preview__btn"
				:to="{ name: 'Project', params: { slug: project.slug || '#' } }"
				:href="project.projectLink"
				:target="project.projectLink ? '_blank' : undefined"
				:title="$t('see')"
			>
				<i class="icon-chevron-right"></i>
			</router-link>
		</div>
	</div>
</template>

<script>
export default {
	props: {
		project: {
			type: Object,
			required: true,
		},
	},
}
</script>

<style lang="scss" scoped>
@import '@/sass/_variables';

$transition: 1s ease-in-out;

.project-preview {
	width: 100%;
	height: 400px;
	position: relative;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	color: #ffffff;
	padding: 0 16px;
	text-decoration: none !important;
	box-sizing: border-box;
	-webkit-mask-image: -webkit-radial-gradient(white, black);
	background-color: #282828;
	&__bg {
		&_back,
		&_front {
			z-index: 0;
			position: absolute;
			width: 100%;
			height: 100%;
			pointer-events: none;
			background-position: center bottom;
			background-repeat: no-repeat;
			background-size: cover;
			will-change: transform;
			transition: $transition;
		}
	}
	&::before,
	&::after {
		content: '';
		display: block;
		position: absolute;
		width: 100%;
		height: 100%;
		transition: $transition;
		z-index: 1;
	}
	&__name {
		position: relative;
		text-align: center;
		z-index: 3;
		.text {
			font-size: 20px;
			line-height: 36px;
			margin-bottom: 4px;
			transition: $transition;
		}
		.name {
			font-weight: bold;
			font-size: 36px;
			line-height: 44px;
			transition: $transition;
		}
		.desc {
			text-align: left;
			font-weight: 500;
			font-size: 13px;
			line-height: 20px;
			padding: 0 20px;
			overflow: hidden;
			height: 0;
			margin-top: 0;
			transition: $transition;
			transform: scale(0.5);
		}
	}
	&__inactivity_mask {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(#000, 0.5);
		transition: $transition;
		z-index: 2;
	}
	&__tags {
		position: absolute;
		z-index: 2;
		width: calc(100% + 24px);
		height: 56px;
		margin: -4px -12px;
		padding: 0 16px;
		top: 24px;
		left: 0;
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		font-size: 16px;
		line-height: 20px;
		font-weight: 500;
		color: rgba(#fff, 0.5);
		transition: $transition;
		overflow: hidden;
		span {
			margin: 4px 12px;
			white-space: nowrap;
		}
	}
	&__btn {
		--size: 64px;
		position: relative;
		display: flex;
		justify-content: center;
		align-items: center;
		text-align: center;
		width: var(--size);
		height: 0;
		color: #fff;
		font-size: 24px;
		line-height: 1;
		text-decoration: none;
		opacity: 0;
		transition: $transition;
		z-index: 2;
		transform: translateY(var(--size));
		margin-left: auto;
		margin-right: auto;
		&::after {
			content: '';
			position: absolute;
			top: 50%;
			left: 50%;
			width: var(--size);
			height: var(--size);
			background: rgba(#fff, 0.3);
			border-radius: 50%;
			transition: box-shadow 0.3s;
			transform: translate(-50%, -50%);
			z-index: 1;
		}
		&:hover,
		&:focus {
			&::after {
				box-shadow: 0px 17px 32px rgba(0, 44, 93, 0.07);
			}
		}
	}
	&:hover {
		color: #ffffff;
		.project-preview__name {
			.desc {
				height: 40px;
				margin-top: 20px;
				transform: scale(1);
			}
		}
		.project-preview__btn {
			height: var(--size);
			opacity: 1;
			transform: none;
			margin-top: 24px;
		}
		.project-preview__inactivity_mask {
			opacity: 0;
		}
		.project-preview__tags {
			color: rgba(#fff, 1);
		}
	}
}
@media (max-width: $md) {
	.project-preview {
		height: 300px;
		&__name {
			.text {
				font-size: 16px;
			}
			.name {
				font-size: 24px;
			}
			.desc {
				display: none;
			}
		}
		&__inactivity_mask {
			opacity: 0;
		}
		&__tags {
			display: none;
		}
		&__btn {
			--size: 56px;
			height: var(--size);
			font-size: 22px;
			opacity: 1;
			transform: none;
			margin-top: 24px;
		}
	}
}

// Персонализация карточек проектов:
.project-preview {
	&.panda {
		background-color: #b6181c;
		&::before {
			background: linear-gradient(
				180deg,
				rgba(1, 2, 4, 0.13) 0%,
				rgba(0, 0, 0, 0.38) 110.5%,
				rgba(0, 0, 0, 0) 110.5%
			);
		}
		.project-preview__bg_front {
			z-index: 1;
		}
	}
	&.luckydo {
		background-color: #780194;
		&::before {
			background: linear-gradient(
				180deg,
				rgba(120, 1, 148, 0.48) 0%,
				#780194 100%
			);
			opacity: 0;
		}
		.project-preview__bg_front {
			height: 100%;
			background-position: center top;
			background-size: auto;
			transform: translateY(200px);
		}
		.project-preview__tags {
			color: rgba(#fff, 0.5);
		}
		&:hover {
			&::before {
				opacity: 1;
			}
			.project-preview__bg_front {
				transform: translateY(50px) scale(1.5);
			}
		}
	}
	&.barel {
		background-color: #f9f9f9;
		color: #020e14;
		&::before {
			background: linear-gradient(
				180deg,
				rgba(249, 249, 249, 0.84) 0%,
				#f9f9f9 100%
			);
			opacity: 0;
		}
		.project-preview__bg_front {
			height: calc(100% + 80px);
			background-size: auto;
			transform: translateY(120px) scale(0.9);
		}
		.project-preview__tags {
			color: rgba(#020e14, 0.5);
		}
		&:hover {
			&::before {
				opacity: 1;
			}
			.project-preview__bg_front {
				transform: translateY(0);
			}
		}
	}
	&.yambox {
		background-color: #1d1d1d;
		&::before {
			background: linear-gradient(
				180deg,
				#282828 0%,
				rgba(40, 40, 40, 0.5) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_front {
			height: calc(100% + 40px);
			background-size: auto;
			transform: translateY(40px);
		}
		&:hover {
			&::before {
				opacity: 1;
			}
			.project-preview__bg_front {
				transform: translateY(0);
			}
			.project-preview__tags {
				color: rgba(#fff, 0.33);
			}
		}
	}
	&.vfinterior {
		background: linear-gradient(
			147.65deg,
			#2b3845 4.93%,
			#38444e 24.04%,
			#36424d 42.71%,
			#21303f 87.19%
		);
		&::before {
			background: linear-gradient(
				180deg,
				#282828 0%,
				rgba(40, 40, 40, 0.75) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_front {
			background-position: right bottom;
			bottom: -1px;
		}
		&:hover {
			&::before {
				opacity: 0.66;
			}
			.project-preview__bg_front {
				transform: scale(1.5) translate(-15%, -15%);
			}
		}
	}
	&.marker {
		background: linear-gradient(90deg, #07ab3f -1.11%, #14a76c 100%);
		&::before {
			background: linear-gradient(
				180deg,
				rgba(31, 103, 71, 0.75) 0%,
				rgba(31, 103, 71, 0.25) 100%
			);
		}
		&::after {
			background: linear-gradient(
				180deg,
				#1b7a50 0%,
				rgba(27, 122, 80, 0.5) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_back {
			background-size: auto;
			background-position: bottom center;
		}
		.project-preview__bg_front {
			height: calc(120% + 60px);
			width: 125%;
			background-size: auto;
			background-position: center bottom;
			transform: scale(0.8) translateY(60px);
		}
		&:hover {
			&::before {
				opacity: 0;
			}
			&::after {
				opacity: 1;
			}
			.project-preview__bg_front {
				background-position: center bottom;
				transform: scale(1) translateY(1px);
			}
		}
	}
	&.stokel {
		background: #ffe310;
		color: #333;
		&::before {
			background: linear-gradient(
				180deg,
				#ffe310 0%,
				rgba(255, 227, 16, 0.74) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_front {
			background-size: auto;
			background-position: right bottom;
			transform: scale(0.5) translate(50%, 50%);
			width: 150%;
			height: 150%;
			bottom: -2px;
			right: -2px;
		}
		.project-preview__tags {
			color: rgba(#4f4f4f, 0.5);
		}
		&:hover {
			&::before {
				opacity: 1;
			}
			.project-preview__bg_front {
				background-size: auto;
				background-position: right bottom;
				transform: scale(1);
			}
			.project-preview__tags {
				color: rgba(#4f4f4f, 1);
			}
		}
	}
	&.roscolor {
		background: #672649;
		&::before {
			background: linear-gradient(
				90.13deg,
				rgba(56, 49, 132, 0.46) 0.07%,
				#e72222 99.93%
			);
			z-index: 1;
		}
		&::after {
			background: linear-gradient(
				180deg,
				rgba(40, 40, 40, 0.75) 0%,
				rgba(40, 40, 40, 0.5625) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_front {
			background-size: auto;
			background-position: right bottom;
			transform: translateY(40px);
			height: calc(100% + 40px);
			bottom: 0;
			z-index: 1;
		}
		&:hover {
			&::after {
				opacity: 1;
			}
			.project-preview__bg_front {
				transform: translateY(0);
			}
		}
	}
	&.ohotta {
		background: linear-gradient(22.89deg, #0062ae 3.67%, #64dd17 83.47%);
		&::before {
			background: linear-gradient(
				180deg,
				#279404 0%,
				rgba(15, 133, 116, 0.69) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_front {
			height: calc(100% + 230px);
			background-size: auto;
			bottom: 0;
			transform: translateY(230px);
		}
		&:hover {
			&::before {
				opacity: 1;
			}
			.project-preview__bg_front {
				transform: translateY(0);
			}
		}
	}
	&.place-to-space {
		background: #131f3c;
		&::before {
			background: linear-gradient(
				180deg,
				rgba(40, 40, 40, 0.75) 0%,
				rgba(40, 40, 40, 0.5625) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_front {
			background-size: auto;
			transform: translateY(95px);
		}
		&:hover {
			&::before {
				opacity: 1;
			}
			.project-preview__bg_front {
				transform: translateY(0);
			}
		}
	}
	&.its-yug {
		background: linear-gradient(95.01deg, #08224f 17.4%, #002c8f 91.28%);
		&::before {
			background: linear-gradient(95.01deg, #08224f 17.4%, #002c8f 91.28%);
			opacity: 0.9;
		}
		&::after {
			background: linear-gradient(
				180deg,
				#0a153c 0%,
				rgba(10, 21, 60, 0.75) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_front {
			background-size: auto;
			transform: scale(0.8) translateY(95px);
			width: 140%;
			z-index: 1;
		}
		&:hover {
			&::after {
				opacity: 1;
			}
			.project-preview__bg_front {
				transform: translateY(0);
			}
		}
	}
	&.mk-electra {
		background: #182d45;
		&::before {
			background: linear-gradient(
				180deg,
				#182d45 35.76%,
				rgba(23, 44, 68, 0) 100%
			);
		}
		&::after {
			background: linear-gradient(
				180deg,
				#182d45 0%,
				rgba(24, 45, 69, 0.7) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_front {
			background-size: auto;
			background-position: bottom right;
			transform: scale(0.6) translate(0, 215px);
			width: 160%;
			height: 160%;
			bottom: 0;
		}
		&:hover {
			&::before {
				opacity: 0.3;
			}
			&::after {
				opacity: 1;
			}
			.project-preview__bg_front {
				transform: scale(1) translate(-140px, 0);
			}
		}
	}
	&.ovik {
		background: #2b5174;
		&::before {
			background: linear-gradient(
				180deg,
				#1b3a55 0%,
				rgba(27, 58, 85, 0.5) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_back {
			background-position: right bottom;
		}
		&:hover {
			&::before {
				opacity: 1;
			}
		}
	}
	&.svoya-brigada {
		background: #949491;
		&::before {
			background: linear-gradient(
				180deg,
				#60646f 0%,
				rgba(96, 100, 111, 0.5) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_back {
			background-position: right top;
			height: calc(100% + 8px);
			width: calc(100% + 8px);
			bottom: -4px;
			right: -4px;
		}
		&:hover {
			.project-preview__bg_back {
				transform: scale(1.2) translateX(-30px);
			}
			&::before {
				opacity: 1;
			}
		}
	}
	&.new-alaska {
		background: #9f9f9f;
		&::before {
			background: rgba(0, 0, 0, 0.33);
		}
		&::after {
			background: linear-gradient(
				180deg,
				#282828 0%,
				rgba(40, 40, 40, 0.5) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_back {
			bottom: -1px;
			height: calc(100% + 2px);
		}
		.project-preview__bg_front {
			background-size: auto;
			background-position: left bottom;
			width: 200%;
			height: 200%;
			left: 0;
			bottom: 0;
			transform: scale(0.7) translate(-22%, 40%);
		}
		&:hover {
			&::after {
				opacity: 1;
			}
			.project-preview__bg_front {
				transform: scale(1) translate(0);
			}
		}
	}
	&.planeta-12 {
		background: #434243;
		&::before {
			background: rgba(0, 0, 0, 0.66);
		}
		&::after {
			background: linear-gradient(
				180deg,
				#282828 0%,
				rgba(40, 40, 40, 0.5) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_front {
			background-size: auto;
			width: 150%;
			transform: scale(0.8) translateY(100px);
			z-index: 1;
		}
		&:hover {
			&::after {
				opacity: 1;
			}
			.project-preview__bg_back {
				transform: scale(1.2);
			}
			.project-preview__bg_front {
				background-size: auto;
				width: 150%;
				transform: scale(1) translateY(0);
				z-index: 1;
			}
		}
	}
	&.checkpoint {
		background: #7c1917;
		&::before {
			background: linear-gradient(
				180deg,
				#282828 0%,
				rgba(40, 40, 40, 0.5) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_back {
			transform: scale(1);
		}
		&:hover {
			&::before {
				opacity: 1;
			}
			.project-preview__bg_back {
				transform: scale(1.3);
			}
		}
	}
	&.stroysnab {
		background: #e1e4ea;
		color: #1d262b;
		.project-preview__name {
			.desc {
				color: #ffffff;
			}
		}
		&::before {
			background: linear-gradient(
				180deg,
				#737373 0%,
				rgba(40, 40, 40, 0.5) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_back {
			background-position: 60% bottom;
			transform: scale(1);
		}
		.project-preview__tags {
			color: rgba(#1d262b, 0.5);
		}
		&:hover {
			color: #fff;
			&::before {
				opacity: 1;
			}
			.project-preview__bg_back {
				transform: scale(1.4);
			}
			.project-preview__tags {
				color: rgba(#fff, 1);
			}
		}
	}
	&.biovillage-app {
		background: linear-gradient(180deg, #ffd12d 0%, #ffb84b 100%);
		color: #0d0d0d;
		.project-preview__tags {
			color: rgba(#020e14, 0.5);
		}
		&::before {
			background: linear-gradient(
				180deg,
				#ffd12d 22%,
				rgba(255, 187, 75, 0.88) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_front {
			height: 200%;
			background-size: 100%;
			background-position: center;
			transform: translateY(25%);
		}
		&:hover {
			&::before {
				opacity: 1;
			}
			.project-preview__bg_front {
				transform: translateY(12%);
			}
			.project-preview__tags {
				color: #0d0d0d;
			}
		}
	}
	&.biovillage-landing {
		background: linear-gradient(180deg, #ffb800 0%, #e89619 100%);
		color: #0d0d0d;
		.project-preview__tags {
			color: rgba(#020e14, 0.5) !important;
		}
		&::before {
			background: linear-gradient(
				180deg,
				#ffd12d 25.25%,
				rgba(255, 184, 75, 0.81) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_front {
			width: 82%;
			height: 200%;
			background-size: 100%;
			background-position: center;
			transform: translateY(155px);
		}
		&:hover {
			&::before {
				opacity: 1;
			}
			.project-preview__bg_front {
				transform: translateY(0);
			}
		}
	}
	&.caristo {
		background: #181c31;
		&::before {
			background: linear-gradient(
				180deg,
				#282828 0%,
				rgba(40, 40, 40, 0.73) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_front {
			height: 200%;
			background-size: 100%;
			background-position: center;
			transform: translateY(20%);
		}
		&:hover {
			&::before {
				opacity: 1;
			}
			.project-preview__bg_front {
				transform: scale(1.5) translateY(6%);
			}
		}
	}
	&.mkm-app {
		background: #04051c;
		&::before {
			background: linear-gradient(
				180deg,
				#282828 0%,
				rgba(40, 40, 40, 0.71) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_back {
			width: 202px;
			height: 202px;
			border-radius: 100%;
			background: linear-gradient(
				0deg,
				#ad6dff 0%,
				#00cffd 105.28%
			) !important;
			bottom: -59px;
		}
		.project-preview__bg_front {
			width: 54px;
			height: 54px;
			bottom: 15px;
			transition: $transition;
		}
		&:hover {
			&::before {
				opacity: 1;
			}
			.project-preview__bg_back {
				transform: scale(3.4);
			}
			.project-preview__bg_front {
				transform: scale(3.5);
			}
		}
	}
	&.mkm-bot {
		background: #04051c;
		&::before {
			background: linear-gradient(
				180deg,
				#282828 0%,
				rgba(40, 40, 40, 0.78) 100%
			);
			opacity: 0;
		}
		.project-preview__bg_back {
			width: 360px;
			height: 360px;
			border-radius: 100%;
			background: #252864;
			bottom: -120px;
		}
		.project-preview__bg_front {
			width: 98px;
			height: 98px;
			bottom: 20px;
			transition: $transition;
		}
		&:hover {
			&::before {
				opacity: 1;
			}
			.project-preview__bg_back {
				transform: scale(2);
			}
			.project-preview__bg_front {
				transform: scale(2.3);
			}
		}
	}
}
</style>

<style lang="scss">
@import '@/sass/_variables';

.projects-grid {
	display: flex;
	flex-wrap: wrap;
	.project-preview {
		width: 33.333333%;
	}
}
@media (max-width: $xl) {
	.projects-grid {
		.project-preview {
			width: 50%;
		}
	}
}
@media (max-width: $md) {
	.projects-grid {
		.project-preview {
			width: 100%;
			margin: 4px 16px;
			&__name {
				margin-bottom: 50px;
			}
		}
	}
}
</style>
